import { c as _c } from "react/compiler-runtime";
import { patchOrgAgentsByAgentIdMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { Card, Dialog, Divider, Form } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AgentIconPicker from '@/components/agent-icon-picker';
import AGENT_ICONS, { type AgentIconKind, DEFAULT_AGENT_ICON } from '@/components/agent-icon-picker/agent-icons';
import { useBuilder } from '../store/provider';
type RenameDialogProps = {
  title: string;
  agentId: string;
  open: boolean;
  onClose: (submitted: boolean) => void;
};
type RenameFormData = {
  name: string;
  icon: AgentIconKind;
};
const parseIcon = (value?: string): AgentIconKind => AGENT_ICONS.includes(value as AgentIconKind) ? value as AgentIconKind : DEFAULT_AGENT_ICON;
const RenameDialog = t0 => {
  const $ = _c(96);
  const {
    title,
    agentId,
    open,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.header.renameDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    state,
    refresh
  } = useBuilder();
  const {
    name,
    icon
  } = state.bundle;
  const showErrorToast = useRequestErrorToast();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = patchOrgAgentsByAgentIdMutation();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== showErrorToast) {
    t3 = {
      ...t2,
      onError: showErrorToast
    };
    $[2] = showErrorToast;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const renameMutation = useMutation(t3);
  const formRef = useRef(null);
  let t4;
  if ($[4] !== icon) {
    t4 = parseIcon(icon);
    $[4] = icon;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== name || $[7] !== t4) {
    t5 = {
      defaultValues: {
        name,
        icon: t4
      }
    };
    $[6] = name;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const form = useForm(t5);
  const {
    reset,
    handleSubmit,
    formState
  } = form;
  let t6;
  let t7;
  if ($[9] !== icon || $[10] !== name || $[11] !== open || $[12] !== reset) {
    t6 = () => {
      if (open) {
        reset({
          name,
          icon: parseIcon(icon)
        });
      }
    };
    t7 = [open, name, icon, reset];
    $[9] = icon;
    $[10] = name;
    $[11] = open;
    $[12] = reset;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] !== agentId || $[16] !== onClose || $[17] !== refresh || $[18] !== renameMutation) {
    t8 = async data => {
      try {
        await renameMutation.mutateAsync({
          path: {
            agentId
          },
          body: {
            name: data.name,
            icon: data.icon
          }
        });
      } catch {
        return;
      }
      await refresh();
      onClose(true);
    };
    $[15] = agentId;
    $[16] = onClose;
    $[17] = refresh;
    $[18] = renameMutation;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const onSubmit = t8;
  let t9;
  if ($[20] !== onClose) {
    t9 = () => onClose(false);
    $[20] = onClose;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t) {
    t10 = t(_temp);
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== formState.isSubmitting || $[25] !== t10) {
    t11 = {
      form: "rename-agent-form",
      label: t10,
      loading: formState.isSubmitting,
      type: "submit"
    };
    $[24] = formState.isSubmitting;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const t12 = formState.isSubmitting;
  let t13;
  if ($[27] !== t) {
    t13 = t(_temp2);
    $[27] = t;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== onClose) {
    t14 = () => onClose(false);
    $[29] = onClose;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== formState.isSubmitting || $[32] !== t13 || $[33] !== t14) {
    t15 = {
      disabled: t12,
      label: t13,
      onClick: t14
    };
    $[31] = formState.isSubmitting;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== handleSubmit || $[36] !== onSubmit) {
    t16 = handleSubmit(onSubmit);
    $[35] = handleSubmit;
    $[36] = onSubmit;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t) {
    t17 = t(_temp3);
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t17) {
    t18 = <p className="text-label-2 text-primary">{t17}</p>;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t) {
    t19 = t(_temp4);
    $[42] = t;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t19) {
    t20 = <p className="text-body-2 text-tertiary">{t19}</p>;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t18 || $[47] !== t20) {
    t21 = <div className="flex flex-1 flex-col gap-1">{t18}{t20}</div>;
    $[46] = t18;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = t23 => {
      const {
        field
      } = t23;
      return <AgentIconPicker value={field.value} onChange={field.onChange} portalContainerRef={formRef} />;
    };
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== form.control) {
    t23 = <div className="flex-1 flex md:justify-end"><Controller name="icon" control={form.control} render={t22} /></div>;
    $[50] = form.control;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t21 || $[53] !== t23) {
    t24 = <div className="flex flex-col gap-3 align-center justify-between md:flex-row md:gap-0">{t21}{t23}</div>;
    $[52] = t21;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Divider />;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = t(_temp5);
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t26) {
    t27 = <p className="text-label-2 text-primary">{t26}</p>;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t) {
    t28 = t(_temp6);
    $[60] = t;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t28) {
    t29 = <p className="text-body-2 text-tertiary">{t28}</p>;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t27 || $[65] !== t29) {
    t30 = <div className="flex flex-1 flex-col gap-1">{t27}{t29}</div>;
    $[64] = t27;
    $[65] = t29;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t) {
    t31 = t(_temp7);
    $[67] = t;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== t) {
    t32 = t(_temp8);
    $[69] = t;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== t) {
    t33 = t(_temp9);
    $[71] = t;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== t33) {
    t34 = {
      required: t33
    };
    $[73] = t33;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] !== t31 || $[76] !== t32 || $[77] !== t34) {
    t35 = <div className="flex-1 min-w-0"><Form.Input size="compact" name="name" aria-label={t31} placeholder={t32} rules={t34} /></div>;
    $[75] = t31;
    $[76] = t32;
    $[77] = t34;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== t30 || $[80] !== t35) {
    t36 = <div className="flex flex-col gap-3 align-center justify-between md:flex-row md:gap-0">{t30}{t35}</div>;
    $[79] = t30;
    $[80] = t35;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== t16 || $[83] !== t24 || $[84] !== t36) {
    t37 = <form ref={formRef} id="rename-agent-form" onSubmit={t16} className="flex flex-col gap-3 p-4">{t24}{t25}{t36}</form>;
    $[82] = t16;
    $[83] = t24;
    $[84] = t36;
    $[85] = t37;
  } else {
    t37 = $[85];
  }
  let t38;
  if ($[86] !== form || $[87] !== t37) {
    t38 = <Card.Root><Card.Body><FormProvider {...form}>{t37}</FormProvider></Card.Body></Card.Root>;
    $[86] = form;
    $[87] = t37;
    $[88] = t38;
  } else {
    t38 = $[88];
  }
  let t39;
  if ($[89] !== open || $[90] !== t11 || $[91] !== t15 || $[92] !== t38 || $[93] !== t9 || $[94] !== title) {
    t39 = <Dialog onClose={t9} open={open} primaryButton={t11} secondaryButton={t15} title={title}>{t38}</Dialog>;
    $[89] = open;
    $[90] = t11;
    $[91] = t15;
    $[92] = t38;
    $[93] = t9;
    $[94] = title;
    $[95] = t39;
  } else {
    t39 = $[95];
  }
  return t39;
};
export default RenameDialog;
function _temp($) {
  return $.cta;
}
function _temp2($_0) {
  return $_0.cancel;
}
function _temp3($_1) {
  return $_1.icon.label;
}
function _temp4($_2) {
  return $_2.icon.description;
}
function _temp5($_3) {
  return $_3.name.label;
}
function _temp6($_4) {
  return $_4.name.description;
}
function _temp7($_5) {
  return $_5.name.label;
}
function _temp8($_6) {
  return $_6.name.placeholder;
}
function _temp9($_7) {
  return $_7.name.required;
}